import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Mic, FileUp, MicOff, Loader, Settings } from 'lucide-react';
import { DocumentContent, Account } from '../types';
import { useHealthScribeStreaming } from '../hooks/useHealthScribeStreaming';
import { requestWakeLock, releaseWakeLock, setupWakeLockVisibilityHandler } from '../utils/screenWakeLock';

interface RecordAudioSectionProps {
  onFilesAdded: (documents: DocumentContent[]) => void;
  onMicClick?: () => void;
  patientId?: string;
  account?: Account;
  noteTemplate?: string;
  taskDefinitionId?: string;
  onRecordingComplete?: (userRequestId: string) => void;
  onRecordingStatusChange?: (isRecordingActive: boolean) => void;
}

export const RecordAudioSection: React.FC<RecordAudioSectionProps> = ({
  onFilesAdded,
  patientId = 'default-patient',
  account,
  noteTemplate = 'PHYSICAL_SOAP',
  taskDefinitionId,
  onRecordingComplete,
  onRecordingStatusChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // HealthScribe streaming integration
  const {
    session,
    isConnected,
    isRecording,
    isPaused,
    startSession,
    startRecording,
    stopRecording,
    stopSession,
    initializeConnection,
    pauseStreaming,
    resumeStreaming
  } = useHealthScribeStreaming();

  // Component state
  const [recordingState, setRecordingState] = useState<'idle' | 'initializing' | 'ready' | 'recording' | 'stopping' | 'completed'>('idle');
  const [error, setError] = useState<string>('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Mic settings state
  const [selectedMicId, setSelectedMicId] = useState<string>('');


  // Keep isRecordingRef updated with current isRecording value
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Refs for waveform visualization
  const timerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isRecordingRef = useRef<boolean>(false);

  const MAX_RECORDING_TIME = 1800;

  // Load saved mic preference on mount
  useEffect(() => {
    const savedMicId = localStorage.getItem('preferredMicId');
    if (savedMicId) {
      setSelectedMicId(savedMicId);
    }
  }, []);

  // Initialize HealthScribe connection when component mounts (only once)
  useEffect(() => {
    if (hasInitialized) return;
    
    const init = async () => {
      setRecordingState('initializing');
      setHasInitialized(true);
      try {
        await initializeConnection();
        setRecordingState('ready');
        setError('');
      } catch (err) {
        setError('Connection has been lost. Trying to reconnect...');
        setRecordingState('ready'); // Still set to 'ready' to allow user to retry manually
      }
    };
    init();
  }, [initializeConnection, hasInitialized]); // Only depends on hasInitialized, runs once

  // Track recording time
  useEffect(() => {
    if (isRecording && recordingState === 'recording' && !isPaused) {
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            handleStopRecording().catch(console.error);
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, recordingState, isPaused]);

  // Track session completion and errors
  useEffect(() => {
    if (session?.status === 'completed' && recordingState === 'stopping') {
      setRecordingState('completed');
      if (session.userRequestId && onRecordingComplete) {
        onRecordingComplete(session.userRequestId);
      }
    }
    
    // Handle session errors by transitioning to ready state and showing error
    if (session?.status === 'error' && recordingState === 'recording') {
      setRecordingState('ready');
      setError(session.error || 'An error occurred during recording');
      isRecordingRef.current = false;
      
      // Stop audio processing immediately - the cleanup will be handled by stopRecording
    }
  }, [session?.status, recordingState, session?.userRequestId, session?.error, onRecordingComplete]);

  // Setup wake lock visibility handler to prevent screen dimming
  useEffect(() => {
    const cleanup = setupWakeLockVisibilityHandler();
    return cleanup;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
      // Release wake lock when component unmounts
      releaseWakeLock();
    };
  }, []);

  // Helper function to convert MIME type to file extension
  const getFileExtension = (mimeType: string, fileName: string): string => {
    const fileNameExtension = fileName.split('.').pop()?.toLowerCase();
    if (fileNameExtension) {
      return fileNameExtension;
    }

    const mimeToExtension: Record<string, string> = {
      'audio/mpeg': 'mp3',
      'audio/mp3': 'mp3',
      'audio/wav': 'wav',
      'audio/wave': 'wav',
      'audio/x-wav': 'wav',
      'audio/mp4': 'm4a',
      'audio/x-m4a': 'm4a',
      'audio/aac': 'aac',
    };

    return mimeToExtension[mimeType] || 'unknown';
  };

  const processFiles = useCallback(async (fileList: File[]) => {
    const validFiles = fileList.filter(file => {
      return file.type.startsWith('audio/') || 
             ['mp3', 'wav', 'm4a', 'aac'].includes(
               file.name.split('.').pop()?.toLowerCase() || ''
             );
    });

    if (validFiles.length === 0) {
      alert('Please select audio files only (MP3, WAV, M4A).');
      return;
    }

    const documents: DocumentContent[] = [];
    
    for (const file of validFiles) {
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const fileExtension = getFileExtension(file.type, file.name);
        
        documents.push({
          name: file.name || `audio_${Date.now()}.${fileExtension}`,
          format: fileExtension,
          content: base64,
        });
      } catch (error) {
        console.error('Error processing file:', file.name, error);
        alert(`Error processing file: ${file.name}`);
      }
    }

    if (documents.length > 0) {
      onFilesAdded(documents);
    }
  }, [onFilesAdded]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    if (e.target) {
      e.target.value = '';
    }
  }, [processFiles]);

  // Recording handlers
  const handleStartRecording = useCallback(async () => {
    try {
      setError('');
      setRecordingState('initializing');
      
      // Check if we have required data
      if (!account) {
        setError('Account information required');
        setRecordingState('ready');
        return;
      }
      
      // Request wake lock to keep screen on during recording
      const wakeLockAcquired = await requestWakeLock();
      if (!wakeLockAcquired) {
        console.warn('Wake lock not acquired, screen may dim during recording');
      }
      
      // Start session and recording
      await startSession(patientId, noteTemplate, account, taskDefinitionId);
      
      // Small delay to ensure session state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Pass selected mic device to recording
      await startRecording(selectedMicId || undefined);
      
      // Start waveform visualization
      setupWaveformVisualization();
      
      setRecordingState('recording');
      setRecordingTime(0);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
      setRecordingState('ready');
      // Release wake lock on error
      await releaseWakeLock();
    }
  }, [account, patientId, noteTemplate, taskDefinitionId, startSession, startRecording]);

  const handleStopRecording = useCallback(async () => {
    try {
      setRecordingState('stopping');
      setError('Processing clinical notes...');
      
      // Release wake lock when stopping recording
      await releaseWakeLock();
      
      await stopRecording();
      await stopSession();
      
      // Stop waveform visualization
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      setError('');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop recording');
      setRecordingState('recording');
      console.error('Error stopping HealthScribe recording:', err);
      // Ensure wake lock is released even on error
      await releaseWakeLock();
    }
  }, [stopRecording, stopSession]);

  // Notify parent when recording state changes so UI can lock navigation
  useEffect(() => {
    if (!onRecordingStatusChange) {
      return;
    }
    const isActive = recordingState === 'recording' || recordingState === 'stopping';
    onRecordingStatusChange(isActive);
  }, [recordingState, onRecordingStatusChange]);

  // Setup waveform visualization (with debug logs)
  const setupWaveformVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount) as Uint8Array<ArrayBuffer>;
      

      // Start real-time waveform visualization
      const updateWaveform = () => {
        // Get current values from refs to avoid stale closure
        const currentAnalyser = analyserRef.current;
        const currentDataArray = dataArrayRef.current;
        const currentIsRecording = isRecordingRef.current;
        
        if (currentAnalyser && currentDataArray && currentIsRecording) {
          currentAnalyser.getByteFrequencyData(currentDataArray as Uint8Array<ArrayBuffer>);
          const waveform = Array.from(currentDataArray).slice(0, 50);
          setWaveformData(waveform);
          animationFrameRef.current = requestAnimationFrame(updateWaveform);
        } else {
          // Keep trying even if not recording yet
          animationFrameRef.current = requestAnimationFrame(updateWaveform);
        }
      };
      updateWaveform();
    } catch (err) {
      console.error('❌ Error setting up waveform visualization:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getWaveformBars = () => {
    
    if (waveformData.length === 0) {
      // Show placeholder bars when no data
      return Array.from({ length: 50 }, (_, i) => (
        <div
          key={i}
          className="w-1 bg-base-300 rounded-full transition-all duration-150"
          style={{ height: '20%' }}
        />
      ));
    }

    return waveformData.map((height, i) => (
      <div
        key={i}
        className="w-1 rounded-full transition-all duration-150"
        style={{ 
          height: `${Math.max(height, 5)}%`,
          backgroundColor: recordingState === 'recording' ? '#2563eb' : '#059669'
        }}
      />
    ));
  };

  // Show different UI based on recording state
  if (recordingState === 'initializing') {
    return (
      <div className="space-y-6">
        <div className="text-left">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Loader className="w-8 h-8 text-primary animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-base-content mb-2">Getting Audio Recorder Ready...</h2>
          <p className="text-base-content/70">Please wait while we initialize the connection</p>
        </div>
      </div>
    );
  }

  if (recordingState === 'recording' || recordingState === 'stopping') {
    return (
      <div className="space-y-6">
        {/* Recording Header */}
        <div className="text-left">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors duration-300 ${
            recordingState === 'stopping' ? 'bg-warning/10' : (isPaused ? 'bg-info/10' : 'bg-error/10 animate-pulse')
          }`}>
            {recordingState === 'stopping' ? (
              <Loader className="w-8 h-8 text-warning animate-spin" />
            ) : (
              <Mic className={`w-8 h-8 ${isPaused ? 'text-info' : 'text-error'}`} />
            )}
          </div>
          <h2 className="text-xl font-semibold text-base-content mb-2">
            {recordingState === 'stopping' ? 'Processing Recording...' : (isPaused ? 'Recording Paused' : 'Recording Active')}
          </h2>
          <p className="text-base-content/70">
            {recordingState === 'stopping' ? 'Generating clinical notes...' : (isPaused ? 'Paused' : 'Speak clearly into your microphone')}
          </p>
        </div>

        {/* Timer (hidden while paused) */}
        {!isPaused && (
          <div className="text-center">
            <div className="text-2xl font-mono font-bold text-base-content">
              {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
            </div>
          </div>
        )}

        {/* Waveform / Paused Message */}
        <div className={`bg-base-200 rounded-lg p-3 transition-all duration-300 overflow-hidden ${
          recordingState === 'recording' ? (isPaused ? 'ring-2 ring-info ring-opacity-50' : 'ring-2 ring-primary ring-opacity-50') : ''
        } relative`}>
          {/* Paused message (fade/scale in) */}
          <div className={`text-center py-6 transition-all duration-300 ease-out transform ${
            isPaused ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none absolute inset-0'
          }`}>
            <div className="max-w-sm mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-info/10 text-info border border-info/20 shadow-sm">
                <div className="w-2 h-2 bg-info rounded-full animate-pulse"></div>
                <span className="font-medium">Recording Paused</span>
              </div>
            </div>
          </div>
          {/* Waveform and indicator (fade/scale out when paused) */}
          <div className={`transition-all duration-300 ease-out transform ${
            isPaused ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
          }`}>
            <div className="flex items-end justify-center gap-1 h-20">
              {getWaveformBars()}
            </div>
            {recordingState === 'recording' && (
              <div className="text-center mt-2 transition-opacity duration-300">
                <div className="inline-flex items-center gap-2 text-primary text-sm font-medium">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  Recording...
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        {(recordingState === 'recording') && (
          <div className="flex items-center gap-3">
            <button
              onClick={isPaused ? resumeStreaming : pauseStreaming}
              className={`btn btn-lg ${isPaused ? 'btn-primary' : 'btn-outline'}`}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={handleStopRecording}
              className="btn btn-error btn-lg"
            >
              <MicOff className="w-5 h-5 mr-2" />
              End Recording
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && session?.status === 'error' && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Default UI (idle/ready/completed state)
  return (
    <div className="space-y-4">
      {/* Main Recording Card */}
      <div className="bg-base-200 rounded-lg p-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/30 rounded-full flex items-center justify-center mb-4 mx-auto">
            <Mic className="w-8 h-8 text-primary-content" />
          </div>
          <h2 className="text-xl font-semibold text-base-content mb-2">Record Audio</h2>
          <p className="text-base-content/70">Record voice notes or dictation</p>
        </div>

        {/* Action Buttons */}
        <div className="text-center mt-6 space-y-4">
          {/* Start Recording Button */}
          <button
            onClick={handleStartRecording}
            disabled={recordingState !== 'ready' || !account || !isConnected || session?.status === 'error'}
            className="btn btn-primary btn-lg"
          >
            <Mic className="w-5 h-5 mr-2" />
            Start Recording
          </button>

          {/* Upload section temporarily hidden - may be re-enabled later */}
          {false && (
            <>
              {/* Or separator */}
              <div className="flex items-center justify-center max-w-xs mx-auto">
                <div className="border-t border-base-300 flex-grow"></div>
                <span className="px-4 text-sm text-base-content/50 bg-base-100">or</span>
                <div className="border-t border-base-300 flex-grow"></div>
              </div>

              {/* Upload Recording Button */}
              <button
                onClick={handleUploadClick}
                className="btn btn-outline btn-lg border-[0.5px]"
              >
                <FileUp className="w-5 h-5 mr-2" />
                Upload Recording
              </button>
            </>
          )}
        </div>
      </div>

      {/* Link to Microphone Settings */}
      <div className="text-center">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            // Navigate to settings and open mic configuration
            window.dispatchEvent(new CustomEvent('openMicConfiguration'));
          }}
          className="text-sm text-base-content/60 hover:text-base-content inline-flex items-center gap-1"
        >
          <Settings className="w-4 h-4" />
          Configure Microphone
        </a>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Session Error Display */}
      {session?.status === 'error' && (
        <div className="alert alert-error">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>⚠️ Connection Error</span>
              <span className="text-sm opacity-75">Connection has been lost</span>
            </div>
            <button 
              className="btn btn-sm btn-outline"
              onClick={async () => {
                try {
                  setError('Restarting session...');
                  await stopSession();
                  await startSession(patientId, noteTemplate, account, taskDefinitionId);
                  setError('');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to restart session');
                }
              }}
            >
              Restart Session
            </button>
          </div>
        </div>
      )}

      {/* Connection Status */}
      {!isConnected && recordingState === 'ready' && (
        <div className="alert alert-warning">
          <span>Trying to reconnect...</span>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}; 